/**
 * FinalizeStep.tsx
 *
 * Build Bar — Step 5: Generate.
 *
 * Centered summary card that shows key statistics about the presentation
 * being generated, then triggers the Celery pipeline via `useFinalize`.
 *
 * States:
 *  1. idle      — Summary card + "Generate PPTX" button.
 *  2. generating — Skeleton loading, "Your presentation is being generated…",
 *                  5-second countdown, and "Go to presentations" escape link.
 *  3. error     — Error toast shown; button re-enabled for retry.
 *
 * After a successful finalize call the component starts a 5-second countdown
 * and then navigates to `/dashboard/presentations`. The "Go to presentations"
 * link lets the user navigate immediately without waiting.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import toast from 'react-hot-toast';

import { Skeleton, Button } from '@/components/ui';
import { extractApiError, api } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import { useFinalize } from '@/api/useWizardV2';
import { ROUTES } from '@/constants';
import styles from './FinalizeStep.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Seconds to wait after a successful finalize call before auto-redirecting. */
const REDIRECT_DELAY_S = 5;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format a number as a currency string (S/ prefix).
 *
 * @param value - Numeric value to format.
 * @returns Formatted string.
 */
function formatCurrency(value: number): string {
  return `S/ ${value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * FinalizeStep — Build bar step 5.
 *
 * Reads presentation summary from useWizardStore and fires the finalize
 * mutation. On success: countdown + redirect. On error: toast + retry.
 */
export const FinalizeStep = () => {
  // ── Router ─────────────────────────────────────────────────────────────────

  const navigate = useNavigate();

  // ── Store ──────────────────────────────────────────────────────────────────

  const {
    presentationId,
    formData,
    slides,
    imageSlots,
    costTables,
    reset,
  } = useWizardStore();

  // ── Local state ────────────────────────────────────────────────────────────

  /** Whether the generation pipeline has been triggered. */
  const [isGenerating, setIsGenerating] = useState(false);

  /** Seconds remaining before the auto-redirect fires. */
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_S);

  // ── API mutation ───────────────────────────────────────────────────────────

  const finalize = useFinalize();

  // ── Countdown logic ────────────────────────────────────────────────────────

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Start the countdown interval that ticks every second.
   * When it reaches 0 it navigates and clears itself.
   */
  const navigateAway = () => {
    reset();
    void navigate(ROUTES.dashboardPresentations);
  };

  const startCountdown = () => {
    setIsGenerating(true);
    setCountdown(REDIRECT_DELAY_S);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          navigateAway();
          return 0;
        }
        return prev - 1;
      });
    }, 1_000);
  };

  /** Clean up the interval on unmount to prevent state updates on dead components. */
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // ── Finalize handler ───────────────────────────────────────────────────────

  /**
   * Trigger the Celery generation pipeline.
   * On success: show a success toast and start the 5-second countdown.
   * On error: show an error toast and keep the button enabled for retry.
   */
  const handleGenerate = async () => {
    if (!presentationId || finalize.isPending || isGenerating) return;

    try {
      // Flush cost tables into deck_json before finalizing. The update-costs
      // endpoint converts CostTableConfig[] into data_cost_table slides and
      // inserts them into deck_json.slides[]. Without this step, cost tables
      // would be lost because they live in a separate store field.
      const currentCostTables = useWizardStore.getState().costTables;
      if (currentCostTables.length > 0) {
        const payload = currentCostTables.map((t) => ({
          title: t.title,
          total: t.total,
          rows: t.rows.map((r) => ({
            service: r.service,
            provider: r.provider,
            base_price: r.basePrice,
            margin_percent: r.marginPercent,
            client_price: r.clientPrice,
          })),
        }));
        await api.post(
          `/api/providers/presentations/${presentationId}/update-costs/`,
          { tables: payload },
        );
      }

      await finalize.mutateAsync({ id: presentationId });
      toast.success('Presentation queued for generation.', { duration: 3_000 });
      startCountdown();
    } catch (err) {
      toast.error(extractApiError(err, 'Could not start generation.'), { duration: 8_000 });
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  /**
   * Number of slides in the deck.
   */
  const slideCount = slides.length;

  /**
   * Number of image slots that completed generation successfully.
   */
  const doneImageCount = Object.values(imageSlots).filter(
    (s) => s.status === 'done',
  ).length;

  /**
   * Grand total across all cost tables.
   */
  const grandTotal = costTables.reduce((sum, t) => sum + t.total, 0);

  /**
   * Friendly label for the selected plantilla slug.
   * Falls back to the raw slug if no mapping is available.
   */
  const plantillaLabel = formData.plantilla_slug || '—';

  /**
   * Friendly labels for the selected theme slugs (comma-separated).
   */
  const themesLabel =
    formData.theme_slugs.length > 0 ? formData.theme_slugs.join(', ') : '—';

  // ── Render: generating state ───────────────────────────────────────────────

  if (isGenerating) {
    return (
      <div className={styles.centeredContainer}>
        <div className={styles.generatingCard} aria-live="polite" aria-label="Generating presentation">
          {/* Skeleton rows simulating a progress indicator */}
          <div className={styles.skeletonGroup} aria-hidden>
            <Skeleton variant="rect" height={20} />
            <Skeleton variant="rect" height={16} />
            <Skeleton variant="rect" height={16} />
          </div>

          <p className={styles.generatingTitle}>Your presentation is being generated...</p>

          {/* Progress bar */}
          <div
            className={styles.progressBarTrack}
            role="progressbar"
            aria-label="Generating presentation"
            aria-valuenow={REDIRECT_DELAY_S - countdown}
            aria-valuemin={0}
            aria-valuemax={REDIRECT_DELAY_S}
          >
            <div
              className={styles.progressBarFill}
              style={{
                width: `${((REDIRECT_DELAY_S - countdown) / REDIRECT_DELAY_S) * 100}%`,
              }}
            />
          </div>

          <p className={styles.countdownText}>
            Redirecting in <strong>{countdown}s</strong>
          </p>

          {/* Immediate navigation link */}
          <button
            type="button"
            className={styles.immediateLink}
            onClick={navigateAway}
            aria-label="Go to the presentations list now"
          >
            Go to presentations now &#8594;
          </button>
        </div>
      </div>
    );
  }

  // ── Render: idle state ─────────────────────────────────────────────────────

  return (
    <div className={styles.centeredContainer}>
      <div className={styles.summaryCard} aria-label="Presentation summary">
        {/* Card header */}
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderDot} aria-hidden />
          <h2 className={styles.cardHeaderTitle}>Your presentation</h2>
        </div>

        {/* Summary rows */}
        <dl className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <dt className={styles.summaryLabel}>Slides</dt>
            <dd className={styles.summaryValue}>{slideCount}</dd>
          </div>

          <div className={styles.summaryRow}>
            <dt className={styles.summaryLabel}>Generated images</dt>
            <dd className={styles.summaryValue}>{doneImageCount}</dd>
          </div>

          <div className={styles.summaryRow}>
            <dt className={styles.summaryLabel}>Template</dt>
            <dd className={styles.summaryValue}>{plantillaLabel}</dd>
          </div>

          <div className={styles.summaryRow}>
            <dt className={styles.summaryLabel}>Theme(s)</dt>
            <dd className={styles.summaryValue}>{themesLabel}</dd>
          </div>

          {formData.client_name && (
            <div className={styles.summaryRow}>
              <dt className={styles.summaryLabel}>Client</dt>
              <dd className={styles.summaryValue}>{formData.client_name}</dd>
            </div>
          )}

          {grandTotal > 0 && (
            <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
              <dt className={styles.summaryLabel}>Grand Total</dt>
              <dd className={`${styles.summaryValue} ${styles.summaryValueTotal}`}>
                {formatCurrency(grandTotal)}
              </dd>
            </div>
          )}
        </dl>

        {/* Divider */}
        <div className={styles.divider} aria-hidden />

        {/* CTA */}
        <div className={styles.ctaRow}>
          <Button
            variant="primary"
            size="lg"
            onClick={() => void handleGenerate()}
            disabled={finalize.isPending || !presentationId}
            aria-label="Generate the PPTX file for the presentation"
          >
            {finalize.isPending ? 'Generating...' : 'Generate PPTX →'}
          </Button>

          {!presentationId && (
            <p className={styles.warningText} role="alert">
              No active presentation. Go back to the start of the wizard.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
